import { useQuery } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { ArrowLeft, CalendarDays, CheckCircle2, Clock, ArrowRight, Sparkles, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

interface ComingSoonDetails {
  pageTitle?: string;
  pageDescription?: string;
  features?: string[];
  expectedDate?: string;
  contactEmail?: string;
  signupEnabled?: boolean;
}

interface ComingSoonData {
  id: string;
  name: string;
  targetSlug: string;
  comingSoonDetails: ComingSoonDetails | null;
  imageUrl: string | null;
  headline: string | null;
  subheadline: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
}

export default function ComingSoonPage() {
  const [match, params] = useRoute("/coming-soon/:slug");
  const slug = params?.slug;

  const { data, isLoading, error } = useQuery<ComingSoonData>({
    queryKey: ['/api/coming-soon', slug],
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-64 w-full rounded-xl mb-8" />
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-6 w-full mb-2" />
          <Skeleton className="h-6 w-2/3 mb-8" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex items-center justify-center">
        <div className="text-center px-4">
          <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
          <p className="text-muted-foreground mb-6">
            This coming soon page doesn't exist or has been removed.
          </p>
          <Link href="/">
            <Button data-testid="button-back-home">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const details = data.comingSoonDetails || {};
  const pageTitle = details.pageTitle || data.headline || `${data.name} - Coming Soon`;
  const pageDescription = details.pageDescription || data.subheadline || "We're working on something exciting. Stay tuned!";
  const features = details.features || [];
  const expectedDate = details.expectedDate;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link href="/">
          <Button variant="ghost" className="mb-6 -ml-2" data-testid="button-back-home">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        {data.imageUrl && (
          <div className="relative rounded-xl overflow-hidden mb-8 shadow-lg">
            <img
              src={data.imageUrl}
              alt={data.name}
              className="w-full h-64 sm:h-80 md:h-96 object-cover"
              data-testid="img-hero"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wide">Coming Soon</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4"
              data-testid="text-page-title"
            >
              {pageTitle}
            </h1>
            <p 
              className="text-lg sm:text-xl text-muted-foreground leading-relaxed"
              data-testid="text-page-description"
            >
              {pageDescription}
            </p>
          </div>

          {expectedDate && (
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full"
              data-testid="badge-expected-date"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="font-medium">Expected: {expectedDate}</span>
            </div>
          )}

          {features.length > 0 && (
            <div 
              className="bg-card border rounded-xl p-6 sm:p-8"
              data-testid="section-features"
            >
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                What to Expect
              </h2>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li 
                    key={index}
                    className="flex items-start gap-3 text-muted-foreground"
                    data-testid={`text-feature-${index}`}
                  >
                    <ArrowRight className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0">
                <Clock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Stay Updated</h3>
                <p className="text-muted-foreground mb-4">
                  We'll announce this launch on our homepage and through our newsletter. 
                  Check back soon or browse our current selection while you wait.
                </p>
                <Link href="/">
                  <Button data-testid="button-browse-products">
                    Browse Current Products
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
